import { captureSentryException } from "@-/dev.improve.haniverse.lib.sentry";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import ensureError from "@-/com.npmjs.ensure-error";
import { animationFrames } from "#animation-frames";

/**
 * Report a throw that escaped a detached scheduler callback (timer / animation frame /
 * microtask). Such a throw runs on a FRESH stack with no `try/catch` above it, so under the
 * React Native New Architecture it unwinds into the C++ `RuntimeScheduler` event loop, reaches
 * `JsErrorHandler` with no handler, and `std::terminate`s the process — a hard native SIGABRT
 * instead of a dismissable red-box (HAN-36). Every helper below funnels its `catch` through
 * here so a stray throw is swallowed + reported instead of aborting the app.
 *
 * The report itself is defended: `captureSentryException` calls `Sentry.captureException`
 * directly with no internal guard, and the Sentry native bridge can be mid-teardown when these
 * deferred callbacks fire — so a throw HERE would re-create the exact fatal condition. Fall back
 * to the logger if Sentry throws, and never let this function itself throw.
 */
function reportScheduledCallbackError(source: string, error: unknown): void {
  const ensuredError = ensureError(error);
  try {
    captureSentryException(ensuredError, { tags: { source } });
  } catch {
    // Sentry bridge unavailable / mid-teardown — reporting must never re-throw into the scheduler.
  }
  try {
    logger(import.meta.location).error(`scheduled callback threw in ${source}`, ensuredError);
  } catch {
    // Logger unavailable — best-effort; swallowing is the whole point.
  }
}

/**
 * `setTimeout` whose callback can never let a throw escape to the runtime scheduler. Use this
 * instead of a bare `setTimeout` for ANY deferred callback that touches navigation, the database,
 * native modules, or anything that can throw — see `reportScheduledCallbackError` for why a bare
 * timer throw is fatal under the New Architecture.
 *
 * `source` is a short, stable tag (e.g. `"learn-stack-reset"`) attached to the Sentry report so a
 * swallowed throw is still diagnosable. Returns the timer handle for `clearTimeout`, exactly like
 * `setTimeout`.
 */
export function safeSetTimeout(
  callback: () => void,
  delayMs: number,
  source: string,
): ReturnType<typeof setTimeout> {
  return setTimeout(() => {
    try {
      callback();
    } catch (error) {
      reportScheduledCallbackError(source, error);
    }
  }, delayMs);
}

/**
 * Run `callback` on an upcoming animation frame (`animationFrames`, i.e. `request-animation-frames`)
 * — the next one, or the `frames`-th from now — and report a throw instead of letting it escape.
 * Prefer this over `safeSetTimeout(fn, 0, …)` when the intent is "run AFTER the current render /
 * navigation commit has settled" (a frame lands just before the next paint, i.e. after React
 * commits) rather than "wait a duration". `frames: 2` also lets that first frame paint.
 *
 * Note: frames do not tick while the app is backgrounded, so only use it for work that is
 * meaningful on-screen. Returns a function that cancels the callback if it has not run yet.
 */
export function safeRequestAnimationFrame(
  callback: (timestamp: number) => void,
  source: string,
  { frames = 1 }: { frames?: number } = {},
): () => void {
  let isCancelled = false;
  const run = async () => {
    let remainingFrames = frames;
    for await (const timestamp of animationFrames()) {
      if (isCancelled) {
        return;
      }
      remainingFrames -= 1;
      if (remainingFrames <= 0) {
        callback(timestamp);
        return;
      }
    }
  };
  run().catch((error: unknown) => {
    reportScheduledCallbackError(source, error);
  });
  return () => {
    isCancelled = true;
  };
}
